/**
 * THE CAST - the roster, read as people.
 *
 * In Narrative the roster is not a list of targets: it is who is in the
 * room. Each combatant may carry a `persona` on the sheet - a line of
 * description, and a disposition towards the party - and the GM can add a
 * person by typing one line ("Watch Captain Brand - curt, trusts the
 * Kill-team"), which makes an ordinary stat block that can be given
 * numbers later.
 *
 * Pure, zero React dependency.
 */
import { emptyTarget } from '../factories';
import { combatantFromStatblock } from '../roster/factories';
import { onSide } from '../roster/team';
import { partySideOf } from '../phase/sides';
import type { Character } from '../character/types';
import type { Combatant } from '../roster/types';
import { peopleOf } from '../roster/inTheFight';

export type Persona = NonNullable<Character['persona']>;

/** How somebody stands towards the party, coldest first. */
export const DISPOSITIONS = ['Hostile', 'Wary', 'Guarded', 'Neutral', 'Well disposed', 'Devoted'] as const;

const MAX_DESCRIPTION = 200;
const MAX_NAME = 60;

export const personaOf = (c: Combatant): Persona => c.sheet.persona ?? {};

/** Only what a persona may hold, trimmed; an empty field is absent. */
function cleaned(persona: Persona): Persona {
  const description = (persona.description ?? '').trim().slice(0, MAX_DESCRIPTION);
  const disposition = (DISPOSITIONS as readonly string[]).includes(persona.disposition ?? '')
    ? persona.disposition
    : '';
  return { ...(description ? { description } : {}), ...(disposition ? { disposition } : {}) };
}

/**
 * The sheet with its persona changed - a NEW sheet (a character is never
 * mutated in place). A persona left with nothing in it is removed, so a
 * sheet nobody described carries no evidence of the feature.
 */
export function withPersona(sheet: Character, patch: Persona): Character {
  const persona = cleaned({ ...sheet.persona, ...patch });
  const { persona: _was, ...rest } = sheet;
  return Object.keys(persona).length > 0 ? { ...rest, persona } : rest;
}

/** "Name - who they are" (or "Name: who they are"), split at the first separator. */
function parseLine(line: string): { name: string; description: string } {
  const at = line.search(/ - |: /);
  if (at < 0) return { name: line.trim(), description: '' };
  return {
    name: line.slice(0, at).trim(),
    description: line
      .slice(at)
      .replace(/^( - |: )/, '')
      .trim(),
  };
}

/**
 * A person from one typed line: an ordinary stat block under that name,
 * with the rest of the line as their description. Null when the line names
 * nobody.
 */
export function personInOneLine(line: string): Combatant | null {
  const { name, description } = parseLine(line);
  if (!name) return null;
  const called = name.slice(0, MAX_NAME);
  const person = combatantFromStatblock({ ...emptyTarget(), name: called }, {}, { name: called });
  return { ...person, sheet: withPersona(person.sheet, { description }) };
}

export interface Cast {
  /** The party, when the GM has named its team. */
  party: Combatant[];
  /** Everyone else on the roster. */
  others: Combatant[];
}

/** The roster as the party and everyone else. With no party named, everyone is "everyone else". */
export function castOf(roster: readonly Combatant[], partyTeam: string | null): Cast {
  // An Object is in neither half (2026-10-26): it is a thing in the story, not one of its cast.
  const people = peopleOf(roster);
  const side = partySideOf(people, partyTeam);
  const inParty = (c: Combatant) => onSide(c, side);
  return { party: people.filter(inParty), others: people.filter((c) => !inParty(c)) };
}
